// A Wixel asset is recognizable from its own URL, which is what makes "Edit in Wixel" offerable on a file
// nothing ever stamped with the FileRecord header's `wixelEditorUrl` (production writes those 4 fields
// nowhere yet — only the dev seed script does). Wixel returns ONE short link per asset and the surface it
// opens is chosen by a trailing segment: bare → preview, `/preview` → the same, `/view` → viewer,
// `/edit` → editor.
//
// Measured against a real link (2026-08-17), which is why the preview is worth deriving: both the bare form
// and `/preview` 302 to the asset's `wixmp.com/wixel-image/…/export.png` and serve `image/png`, so either is
// usable directly as an image source, while `/edit` 302s to the `manage.wix.com/wixel-new/…/wixel-editor`
// deep link. `/preview` is the form used here because it says what it is at the call site.
//
// Mirrors native `WixelLink.swift` / `WixelLink.kt` byte-for-byte — the host set, the `/link/<slug>` shape,
// the tolerated trailing segments and the https-only rule are one contract across the three surfaces.

import { parseFileRecordContent } from './file-record';
import { extractUrl } from './artifact-url';

/** Segments Wixel itself appends to a short link. A stored URL may already carry one, so it is replaced
 *  rather than appended to — `/link/abc/view/edit` addresses nothing. */
const SURFACE_SEGMENTS = new Set(['edit', 'view', 'preview']);

function isWixelHost(host: string): boolean {
  return host === 'wixel.com' || host.endsWith('.wixel.com');
}

/** `<host>/link/<slug>` for a Wixel short link, or null when `rawUrl` is not one — the single parser both
 *  surface helpers below share, so they can never disagree about what counts as a Wixel asset.
 *
 *  https only: an http link would open the asset over a downgraded connection, and every link Wixel hands
 *  out is https. Query and hash are dropped — a short link carries none, and forwarding a `?token=` loan
 *  onto an editor address would be meaningless. */
function wixelShortLink(rawUrl: string | null | undefined): { host: string; slug: string } | null {
  if (!rawUrl) return null;
  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || !isWixelHost(url.hostname.toLowerCase())) return null;
  const segments = url.pathname.split('/').filter(Boolean);
  if (segments[0] !== 'link' || !segments[1]) return null;
  if (segments.length > 3) return null;
  if (segments.length === 3 && !SURFACE_SEGMENTS.has(segments[2].toLowerCase())) return null;
  return { host: url.hostname, slug: segments[1] };
}

/** The editor URL for a Wixel short link, or null when `rawUrl` is not one. */
export function wixelEditUrlFromAssetUrl(rawUrl: string | null | undefined): string | null {
  const link = wixelShortLink(rawUrl);
  return link && `https://${link.host}/link/${link.slug}/edit`;
}

/** The preview-image URL for a Wixel short link, or null when `rawUrl` is not one. */
export function wixelPreviewUrlFromAssetUrl(rawUrl: string | null | undefined): string | null {
  const link = wixelShortLink(rawUrl);
  return link && `https://${link.host}/link/${link.slug}/preview`;
}

/** Where "Edit in Wixel" should point for a file's stored content, or null when the file is not a Wixel
 *  asset. An explicit `wixelEditorUrl` wins — it names the real editor deep-link (project + asset), which a
 *  short link only redirects to — and the URL the file points at is the fallback. */
export function wixelEditUrl(content: string | null | undefined): string | null {
  if (!content) return null;
  const { header } = parseFileRecordContent(content);
  const explicit = header?.wixelEditorUrl?.trim();
  if (explicit) return explicit;
  return wixelEditUrlFromAssetUrl(extractUrl(content));
}

/** The thumbnail to show for a file's stored content when that file is a Wixel asset, else null. An explicit
 *  `wixelPreviewUrl` wins for the same reason the editor link does — it names a real stored image, where this
 *  is a redirect Wixel resolves per request. */
export function wixelPreviewUrl(content: string | null | undefined): string | null {
  if (!content) return null;
  const { header } = parseFileRecordContent(content);
  const explicit = header?.wixelPreviewUrl?.trim();
  if (explicit) return explicit;
  return wixelPreviewUrlFromAssetUrl(extractUrl(content));
}
